// @ts-strict-ignore
import clsx from 'clsx';
import * as React from 'react';

import {isTestEnv} from 'common/report/util/env';
import {ReportMessageBannerReact} from 'common/widgets/report-message-banner';
import {ReportBannersReact} from 'common/widgets/report-banners';
import {getDownloadModeFromLocation} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {useReportLayoutManager} from 'common/contexts/react/report-layout-manager-context';

import styles from './index.module.styl';

export const HORIZONTAL_CONTENT_PADDING_STYLE = `32px`;

type BannerLayoutProps = React.PropsWithChildren<{
  className?: string;
  id?: string;
  revealedBannerIdentifiers?: Set<string>;
  onBannerLayoutResized?: (_: {padding: number}) => void;
  excludePaddings?: {
    banner?: boolean;
    app?: boolean;
  };
  style?: React.CSSProperties;
}>;

/**
 * BannerLayout functionality:
 * - Adds banners to the top of reports
 * - Is the scroll container for most reports. (It is not used as the scroll container for core reports, since they
 *  have different scroll behavior.)
 * - Adds standardized padding to reports.
 */
export const BannerLayout: React.FC<BannerLayoutProps> = (props: BannerLayoutProps) => {
  const downloadMode = getDownloadModeFromLocation();
  const reportLayoutManager = useReportLayoutManager();

  if (downloadMode !== DownloadMode.None) {
    return <div className={props.className}>{props.children}</div>;
  }

  const contentScrollerRef = React.useRef<HTMLDivElement>();
  React.useEffect(() => {
    if (contentScrollerRef.current) {
      reportLayoutManager.updateScrollContainer(contentScrollerRef.current);
    }
  }, [contentScrollerRef.current]);

  return (
    <div id={props.id} className={clsx(styles.bannerLayout, props.className, `inherit-size`)}>
      <div
        id={props.id}
        className={clsx(styles.contentScroller, `inherit-size`, {
          [styles.excludeAppPadding]: props.excludePaddings?.app,
          [styles.excludeBannerPadding]: props.excludePaddings?.banner,
        })}
        style={{
          ...props.style,
          '--horizontal-padding': HORIZONTAL_CONTENT_PADDING_STYLE,
        }}
        elref={`contentScroller`}
        ref={contentScrollerRef}
      >
        {!isTestEnv() && (
          // only show banners if it's not test env, since some test suites aren't setup to mock banner
          // API requests
          <div className={styles.bannerContainer}>
            <ReportMessageBannerReact />
            <ReportBannersReact revealedBannerIdentifiers={props.revealedBannerIdentifiers} />
          </div>
        )}
        {props.children}
      </div>
    </div>
  );
};
